import { FormBuilder, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormValidation } from '../../classes/formValidation';
import { IDripApplicationRate, IMicroSprinklerSettingViewModel, IPlantingSettingsCalculatorSettings,
	ISprinklerApplicationRateViewModel } from './interfaces';

export type CompostFields = 'Amount' | 'ApplicationMethod' | 'Id' |
	'LotPlantingId' | 'N';

export type CoverCropFields = 'Id' | 'IncorporationMethod' |
	'LegumeCoverCropStand' | 'LotPlantingId';

export type DripApplicationRateFields = 'Id' | 'BedWidth' | 'DripLinesPerBed'
	| 'EmitterDischargeRate' | 'EmittersPerTree' | 'InRowTreeSpacing'
	| 'LotPlantingId' | 'RowSpacing' | 'TapeDischargeRate' | 'TreesPerAcre'
	| 'UseTreesPerAcre';

export type ManureNFields = 'Id' | 'LotPlantingId' | 'TypePriorYear' |
	'RatePriorYear' | 'TypeTwoYearsAgo' | 'RateTwoYearsAgo';


export type MicroSprinklerFields =  'BetweenRowTreeSpacing' | 'ColorId'	|
	'FlowRate' | 'InRowTreeSpacing' | 'ManufacturerId' | 'MicroSprinklerId' |
	'MicroSprinklersPerTree' | 'ModelId' | 'PressureName' | 'SeriesId' |
	'UseFlowRate';

export type SprinklerSettingFields = 'HeadSpacing' | 'Id' | 'LotPlantingId' |
	'NozzleDiameter' | 'NozzlePressure' |  'PipeSpacing' | 'Type';

export class CalculatorSettingsForm {
	private _fb: FormBuilder;
	private _form: FormGroup;
	private _subscriptions$: Subject<boolean>;

	public get dripApp(): { [key in DripApplicationRateFields]: AbstractControl } {
		return (this._form.get('DripApplicationRate') as FormGroup).controls as
			{ [key in DripApplicationRateFields]: AbstractControl };
	}

	public get microSprinkler(): { [key in MicroSprinklerFields]: AbstractControl } {
		return (this._form.get('MicroSprinklerSetting') as FormGroup).controls as
			{ [key in MicroSprinklerFields]: AbstractControl };
	}

	constructor(fb: FormBuilder, $subscription: Subject<boolean>) {
		this._fb = fb;
		this._subscriptions$ = $subscription;
	}

	public initialize(calculatorSettings: IPlantingSettingsCalculatorSettings): FormGroup {
		let sprinkler: ISprinklerApplicationRateViewModel;
		let microSprinkler: IMicroSprinklerSettingViewModel;
		let drip: IDripApplicationRate;

		if (!calculatorSettings) {
			throw new Error('calculationSettings is empty');
		}

		sprinkler = calculatorSettings.SprinklerApplicationRate;
		microSprinkler = calculatorSettings.MicroSprinklerSetting;
		drip = calculatorSettings.DripApplicationRate;

		this._form = this._fb.group({
			CompostN: this._fb.group({
				Amount: [calculatorSettings.CompostN.Amount],

				ApplicationMethod: [calculatorSettings.CompostN.ApplicationMethod ?
					calculatorSettings.CompostN.ApplicationMethod : 0],

				Id: [calculatorSettings.CompostN.Id],
				LotPlantingId: [calculatorSettings.CompostN.LotPlantingId],

				N: [calculatorSettings.CompostN.N],
			}),
			CoverCrop: this._fb.group({
				Id: [calculatorSettings.CoverCrop.Id],
				LotPlantingId: [calculatorSettings.CoverCrop.LotPlantingId],

				LegumeCoverCropStand: [calculatorSettings.CoverCrop.LegumeCoverCropStand ?
					calculatorSettings.CoverCrop.LegumeCoverCropStand : 0],

				IncorporationMethod: [calculatorSettings.CoverCrop.IncorporationMethod ?
					calculatorSettings.CoverCrop.IncorporationMethod : 0]
			}),
			DripApplicationRate: this._fb.group({
				Id: [drip.Id],
				LotPlantingId: [drip.LotPlantingId],
				EmitterDischargeRate: [drip.EmitterDischargeRate],
				EmittersPerTree: [drip.EmittersPerTree],
				RowSpacing: [drip.RowSpacing],
				InRowTreeSpacing: [drip.InRowTreeSpacing],
				TreesPerAcre: [drip.TreesPerAcre],

				UseTreesPerAcre: [drip.UseTreesPerAcre ?
					drip.UseTreesPerAcre : false],

				BedWidth: [drip.BedWidth ? drip.BedWidth : null],

				DripLinesPerBed: [drip.DripLinesPerBed ?
					drip.DripLinesPerBed : null],

				TapeDischargeRate: [drip.TapeDischargeRate ?
					drip.TapeDischargeRate : null],
			}),
			ManureN: this._fb.group({
				Id: [calculatorSettings.ManureN.Id],
				LotPlantingId: [calculatorSettings.ManureN.LotPlantingId],
				RatePriorYear: [calculatorSettings.ManureN.RatePriorYear],
				RateTwoYearsAgo: [calculatorSettings.ManureN.RateTwoYearsAgo],
				TypePriorYear: [calculatorSettings.ManureN.TypePriorYear],
				TypeTwoYearsAgo: [calculatorSettings.ManureN.TypeTwoYearsAgo],
			}),
			MicroSprinklerSetting: this._fb.group({
				BetweenRowTreeSpacing: [microSprinkler.BetweenRowTreeSpacing ?
					microSprinkler.BetweenRowTreeSpacing : null],

				FlowRate: [microSprinkler.FlowRate ? microSprinkler.FlowRate : null],
				Id: [microSprinkler.Id],

				InRowTreeSpacing: [microSprinkler.InRowTreeSpacing ?
					microSprinkler.InRowTreeSpacing : null],

				LotPlantingId: [microSprinkler.LotPlantingId],
				MicroSprinklerId: [microSprinkler.MicroSprinklerId],
				MicroSprinklersPerTree: [microSprinkler.MicroSprinklersPerTree ?
					microSprinkler.MicroSprinklersPerTree : 1, [FormValidation.integerValidator()]],

				UseFlowRate: [microSprinkler.UseFlowRate ?
					microSprinkler.UseFlowRate : false],

				// fields that won't be saved
				ColorId: [ null ],
				ManufacturerId: [ null ],
				ModelId: [ null ],
				PressureName: [ null ],
				SeriesId: [ null ],
			}),
			SprinklerApplicationRate: this._fb.group({
				HeadSpacing: [sprinkler.HeadSpacing ? sprinkler.HeadSpacing : null],
				Id: [sprinkler.Id ? sprinkler.Id : null],
				NozzleDiameter: [sprinkler.NozzleDiameter ? sprinkler.NozzleDiameter : null],
				NozzlePressure: [sprinkler.NozzlePressure ? sprinkler.NozzlePressure : null],
				LotPlantingId: [sprinkler.LotPlantingId],
				PipeSpacing: [sprinkler.PipeSpacing ? sprinkler.PipeSpacing : null],
				Type: [sprinkler.Type]
			})
		});

		this._bindValueChangesTrees();

		return this._form;
	}

	/**
	 * Note: We don't call this method on form initialization because calculator
	 * fields are only required when the calculators are open
	 */
	private _configureFlowRateValidation(): void {
		// if UseFlowRate is true (or undefined), microSprinklerId (and other fields) should be null and not required.
		// if UseFlowRate is false, microSprinklerId (and other fields) should not be null, and should be required.
		if (this.microSprinkler.UseFlowRate.value === true) {
			FormValidation.clearValidator(this.microSprinkler.ManufacturerId);
			FormValidation.clearValidator(this.microSprinkler.ColorId);
			FormValidation.clearValidator(this.microSprinkler.PressureName);
			FormValidation.clearValidator(this.microSprinkler.SeriesId);

			this.microSprinkler.FlowRate.setValidators([Validators.required,
				FormValidation.greaterThanValidator(0)]);
		} else {
			this.microSprinkler.ManufacturerId.setValidators(Validators.required);
			this.microSprinkler.ColorId.setValidators(Validators.required);
			this.microSprinkler.PressureName.setValidators(Validators.required);
			this.microSprinkler.SeriesId.setValidators(Validators.required);

			FormValidation.clearValidator(this.microSprinkler.FlowRate);
			this.microSprinkler.FlowRate.setValue(0, { emitEvent: false} );
		}
	}

	private _bindValueChangesTrees(): void {
		this.microSprinkler.UseFlowRate.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this._configureFlowRateValidation();
		});
	}
}
